import { useContext, useMemo } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import WorkTypeFolderTile from '@atom/components/common/workTemplateFolderTree/WorkTypeFolderTile';
import { useSchemas } from '@atom/hooks/useSchemas';
import { Icon, Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { InventorySchemaItem } from '@atom/types/inventory';
import { TYPE_OPTIONS, WorkOrderType } from '@atom/types/work';
import { WorkTemplateFolderTree } from '@atom/types/workTemplate';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import WorkTemplateContext from '../../WorkTemplateContext';
import WorkTemplateTypeOption from '../workTemplateOption/WorkTemplateTypeOption';

import classes from './workTemplateChildCreate.module.css';

const { MenuItem } = Select;

const EMPTY_SCHEMA = 'empty';

const styles = {
  textFieldStyle: {
    marginLeft: '1rem',
    color: colors.neutral.dark,
  },
  icon: {
    marginTop: '1rem',
  },
  back: {
    marginRight: '1rem',
  },
};

enum View {
  MAIN = 'MAIN',
  WORK_TYPE_FOLDER = 'WORK_TYPE_FOLDER',
  INHERITANCE = 'INHERITANCE',
}

interface Props {
  name: string;
  setName: (name: string) => void;
  error: string;
  workTemplateFolder: WorkTemplateFolderTree;
  setWorkTemplateFolder: (workTemplateFolder: WorkTemplateFolderTree) => void;
  schemaId: string;
  setView: (view: View) => void;
  setError: (error: string) => void;
}

const ChildCreateMain = ({
  name,
  setName,
  error,
  workTemplateFolder,
  setWorkTemplateFolder,
  schemaId,
  setView,
  setError,
}: Props) => {
  const { schemas = [] } = useSchemas();
  const { workTemplate } = useContext(WorkTemplateContext);

  const locationBasedSchemas = useMemo(() => {
    return schemas.filter(schema => !schema.isMaterial);
  }, [schemas]);

  const handleNameChange = event => {
    setName(event.target.value);
    setError('');
  };

  return (
    <div className={classes.createContainer}>
      <div className={classes.sectionHeader}>Details</div>
      <div className={classes.modalRow}>
        <img style={styles.icon} src={renameIcon} />
        <TextField
          id="name"
          label="Name *"
          value={name}
          onChange={handleNameChange}
          style={styles.textFieldStyle}
          error={!!error}
          helperText={error}
        />
      </div>
      <div className={clsx(classes.modalRow, classes.folder)}>
        <Icon color={colors.neutral.gray}>folder</Icon>
        {!R.isNil(workTemplateFolder) ? (
          <div className={classes.tileContainer}>
            <WorkTypeFolderTile
              workTemplateFolder={workTemplateFolder}
              removeWorkTemplateFolder={() => setWorkTemplateFolder(null)}
            />
          </div>
        ) : (
          <div
            className={classes.chooseButton}
            onClick={() => setView(View.WORK_TYPE_FOLDER)}
          >
            Choose Folder *
          </div>
        )}
      </div>
      <div className={classes.sectionHeader}>
        <div>Template Type</div>
        <div className={classes.sectionSubtext}>
          Child Work Template will inherit the type from the parent.
        </div>
      </div>
      <div className={classes.typeContainer}>
        <WorkTemplateTypeOption option={TYPE_OPTIONS[workTemplate?.type]} />
      </div>
      {workTemplate?.type === WorkOrderType.DEFAULT && (
        <div className={classes.modalRow}>
          <Icon color={colors.neutral.gray} style={styles.icon}>
            category
          </Icon>
          <Select
            value={schemaId}
            style={styles.textFieldStyle}
            onChange={() => {}}
            label="Asset Type"
            disabled
            fullWidth
          >
            <MenuItem key="empty" value={EMPTY_SCHEMA}>
              <div className={classes.schemaMenuItemContainer}>
                <div className={classes.emptyIcon}>-</div>
                <div
                  className={clsx(classes.schemaMenuItemTitle, classes.empty)}
                >
                  All Asset Types
                </div>
              </div>
            </MenuItem>
            {locationBasedSchemas.map((schema: InventorySchemaItem) => {
              const icon =
                schemaUtilities.getSchemaIconFromSchemaOrAsset(schema);

              return (
                <MenuItem key={schema.id} value={schema.id}>
                  <div className={classes.schemaMenuItemContainer}>
                    {icon}
                    <div className={classes.schemaMenuItemTitle}>
                      {schema.name}
                    </div>
                  </div>
                </MenuItem>
              );
            })}
          </Select>
        </div>
      )}
    </div>
  );
};

export default ChildCreateMain;
